import { useCallback, type ComponentProps } from "react";
import { GripVertical } from "lucide-react";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortable, useSortable } from "@dnd-kit/react/sortable";
import { cn } from "@/lib/utils";
import type { DirectiveChipSegmentButtonFn } from "../../types/directiveChipTypes";

const PRINT_TABLE_SORTABLE_GROUP = "print-table-columns";

type DragDropProviderOnDragEnd = NonNullable<
  ComponentProps<typeof DragDropProvider>["onDragEnd"]
>;
type PrintTableDragEndEvent = Parameters<DragDropProviderOnDragEnd>[0];

const SortablePrintTableColumnChip = ({
  sortableId,
  index,
  columnLabel,
  segmentButton,
}: {
  /** Stable identity for dnd-kit + React (column label); must be unique within this chip row. */
  sortableId: string;
  index: number;
  columnLabel: string;
  segmentButton: DirectiveChipSegmentButtonFn;
}) => {
  const { ref, handleRef, isDragging } = useSortable({
    id: sortableId,
    index,
    group: PRINT_TABLE_SORTABLE_GROUP,
  });

  return (
    <span
      ref={ref}
      className={cn(
        "inline-flex items-center align-middle",
        isDragging && "opacity-60",
      )}
    >
      <span className="opacity-70">:</span>
      <span
        ref={handleRef}
        data-print-table-column-drag-handle=""
        className="
          inline-flex cursor-grab touch-none items-center px-0.5
          active:cursor-grabbing
        "
        aria-label="Drag to reorder column"
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <GripVertical className="size-3.5 shrink-0" />
      </span>
      {segmentButton(index, columnLabel)}
    </span>
  );
};

/**
 * Horizontal sortable column segments for @print(table)/variable:colA:colB:…
 * Drag handle avoids conflicting with click-to-edit on each segment.
 *
 * Sortable `id` / React `key` use each choice string so instances stay stable across reorders.
 * Duplicate column labels in the same row would collide; print(table) UI should pick distinct columns.
 */
export const PrintTableColumnChips = ({
  choices,
  onReorder,
  segmentButton,
}: {
  choices: readonly string[];
  onReorder: (fromIndex: number, toIndex: number) => void;
  segmentButton: DirectiveChipSegmentButtonFn;
}) => {
  const handleDragEnd = useCallback(
    (event: PrintTableDragEndEvent) => {
      if (event.canceled === true) return;
      const { source } = event.operation;
      if (source == null) return;
      if (!isSortable(source)) return;
      const { initialIndex, index } = source.sortable;
      if (initialIndex === index) return;
      onReorder(initialIndex, index);
    },
    [onReorder],
  );

  return (
    <DragDropProvider onDragEnd={handleDragEnd}>
      <span className="inline-flex flex-wrap items-center align-middle">
        {choices.map((c, i) => (
          <SortablePrintTableColumnChip
            key={c}
            sortableId={c}
            index={i}
            columnLabel={c}
            segmentButton={segmentButton}
          />
        ))}
      </span>
    </DragDropProvider>
  );
};
